import React, { useState, useEffect } from 'react';
import { useOrganizations } from '../hooks/useOrganizations';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onboardingService, OnboardingData } from '../services/onboardingService';
import { Package } from '../types/package';
import { usePackages } from '../hooks/usePackages';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from '../components/icons/Logo';
import EmailVerificationBanner from '../components/auth/EmailVerificationBanner';
import { useForm } from 'react-hook-form';
import {
  BuildingOfficeIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  SparklesIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface OnboardingFormData {
  orgName: string;
  taxNumber?: string;
  industry?: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactPosition: string;
}

const STEPS = [
  { id: 0, name: 'stepOrganization', icon: BuildingOfficeIcon },
  { id: 1, name: 'stepPackage', icon: CreditCardIcon },
  { id: 2, name: 'stepConfirmation', icon: CheckCircleIcon },
];

const Onboarding = () => {
  const { user, signOut, setActiveOrganization } = useAuth();
  const { organizations: myOrganizations, loading: myOrgsLoading } = useOrganizations();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // Van már szervezete? Akkor nem onboarding kell, hanem a vezérlőpult.
  const forceNew = searchParams.get('new') === '1';
  useEffect(() => {
    if (forceNew || !user || user.role === 'customer') return;
    if (user.organizationId) { navigate('/', { replace: true }); return; }
    if (!myOrgsLoading && myOrganizations.length > 0) {
      (setActiveOrganization ? setActiveOrganization(myOrganizations[0].id) : Promise.resolve())
        .finally(() => navigate('/', { replace: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organizationId, myOrgsLoading, myOrganizations.length, forceNew]);
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const { packages, loading: packagesLoading } = usePackages();

  useEffect(() => {
    if (packages.length > 0 && !selectedPackageId) {
      const planFromUrl = searchParams.get('plan');
      const planFromStorage = localStorage.getItem('selectedPlan');
      const plan = planFromUrl || planFromStorage;
      
      if (plan) {
        const matchByType = packages.find(p => p.type === plan);
        const matchById = packages.find(p => p.id === plan);
        const match = matchByType || matchById;
        if (match) {
          setSelectedPackageId(match.id);
          if (planFromStorage) localStorage.removeItem('selectedPlan');
          return;
        }
      }
      
      const popular = packages.find(p => p.isPopular);
      setSelectedPackageId(popular?.id || packages[0].id);
    }
  }, [packages, selectedPackageId, searchParams]);

  const selectedPackage = packages.find(p => p.id === selectedPackageId) || null;

  const { register, handleSubmit, formState: { errors }, watch } = useForm<OnboardingFormData>({
    defaultValues: {
      contactName: user?.displayName || '',
      contactEmail: user?.email || '',
      country: 'Magyarország',
    }
  });

  const orgName = watch('orgName');

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onComplete = async (formData: OnboardingFormData) => {
    if (!user) return;
    
    setIsSubmitting(true);
    try {
      const onboardingData: OnboardingData = {
        orgName: formData.orgName,
        taxNumber: formData.taxNumber || '',
        industry: formData.industry || '',
        address: {
          street: formData.street,
          city: formData.city,
          postalCode: formData.postalCode,
          country: formData.country,
        },
        contact: {
          name: formData.contactName,
          email: formData.contactEmail,
          phone: formData.contactPhone,
          position: formData.contactPosition,
        },
        packageType: selectedPackage?.type || 'free',
        packageId: selectedPackageId,
      };

      await onboardingService.createOrganizationWithSubscription(
        user.uid,
        user.email || null,
        user.displayName || null,
        onboardingData
      );

      toast.success(t('saas.wizard.successMsg'));
      setIsCompleted(true);
    } catch (error: any) {
      console.error('[Onboarding] Error:', error);
      toast.error(t('saas.wizard.errorMsg') + ': ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    if (currentStep === 0) {
      return !!orgName && !errors.orgName;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-[#f7f7f9] dark:bg-[#0b0d12] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center space-x-3">
          <Logo className="h-10" />
        </div>
        <button
          onClick={async () => { await signOut(); navigate('/login'); }}
          className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {t('saas.wizard.signOut')}
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-3xl space-y-4">
          <EmailVerificationBanner />
          {isCompleted ? (
            <div className="bg-white dark:bg-[#12151c] rounded-2xl shadow-sm border border-gray-200 dark:border-[#2a2f3c] p-8 sm:p-12 text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center">
                  <CheckCircleIcon className="h-12 w-12 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                {t('saas.wizard.successTitle')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                {t('saas.wizard.successSubtitle')}
              </p>
              <button
                onClick={() => window.location.href = '/'}
                className="inline-flex items-center px-8 py-3 text-sm font-medium text-white bg-[#0f7f75] hover:bg-[#0c6b62] rounded-lg  transition-all"
              >
                {t('saas.wizard.startUsing')}
                <ArrowRightIcon className="h-4 w-4 ml-2" />
              </button>
            </div>
          ) : (
          <>
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                      index <= currentStep
                        ? 'bg-[#0f7f75] border-[#0f7f75] text-white'
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
                    }`}>
                      <step.icon className="h-5 w-5" />
                    </div>
                    <span className={`ml-2 text-sm font-medium hidden sm:block ${
                      index <= currentStep ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'
                    }`}>
                      {t(`saas.wizard.${step.name}`)}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 transition-all duration-300 ${
                      index < currentStep ? 'bg-[#0f7f75]' : 'bg-gray-300 dark:bg-gray-600'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Card */}
          <div className="bg-white dark:bg-[#12151c] rounded-2xl shadow-sm border border-gray-200 dark:border-[#2a2f3c] p-6 sm:p-8">
            <form onSubmit={handleSubmit(onComplete)}>
              {/* Step 0: Organization */}
              {currentStep === 0 && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                      {t('saas.wizard.orgTitle')}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('saas.wizard.orgSubtitle')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                      {t('saas.wizard.orgName')} *
                    </label>
                    <input
                      type="text"
                      {...register('orgName', { required: t('saas.wizard.orgNameRequired') })}
                      className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                      placeholder={t('saas.wizard.orgNamePlaceholder')}
                    />
                    {errors.orgName && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                        {errors.orgName.message}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                        {t('saas.wizard.taxNumber')}
                      </label>
                      <input
                        type="text"
                        {...register('taxNumber')}
                        className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                        placeholder={t('saas.wizard.taxNumberPlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                        {t('saas.wizard.industry')}
                      </label>
                      <select
                        {...register('industry')}
                        className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                      >
                        <option value="">{t('saas.wizard.industryPlaceholder')}</option>
                        <option value="it">{t('saas.wizard.industryIt')}</option>
                        <option value="marketing">{t('saas.wizard.industryMarketing')}</option>
                        <option value="consulting">{t('saas.wizard.industryConsulting')}</option>
                        <option value="trade">{t('saas.wizard.industryTrade')}</option>
                        <option value="manufacturing">{t('saas.wizard.industryManufacturing')}</option>
                        <option value="construction">{t('saas.wizard.industryConstruction')}</option>
                        <option value="services">{t('saas.wizard.industryServices')}</option>
                        <option value="healthcare">{t('saas.wizard.industryHealthcare')}</option>
                        <option value="education">{t('saas.wizard.industryEducation')}</option>
                        <option value="finance">{t('saas.wizard.industryFinance')}</option>
                        <option value="other">{t('saas.wizard.industryOther')}</option>
                      </select>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('saas.wizard.addressTitle')}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          {t('saas.wizard.street')}
                        </label>
                        <input
                          type="text"
                          {...register('street')}
                          className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                          placeholder={t('saas.wizard.streetPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          {t('saas.wizard.city')}
                        </label>
                        <input
                          type="text"
                          {...register('city')}
                          className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                          placeholder={t('saas.wizard.cityPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          {t('saas.wizard.postalCode')}
                        </label>
                        <input
                          type="text"
                          {...register('postalCode')}
                          className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                          placeholder={t('saas.wizard.postalCodePlaceholder')}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          {t('saas.wizard.country')}
                        </label>
                        <input
                          type="text"
                          {...register('country')}
                          className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('saas.wizard.contactTitle')}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          {t('saas.wizard.contactName')}
                        </label>
                        <input
                          type="text"
                          {...register('contactName')}
                          className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                          placeholder={t('saas.wizard.contactNamePlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          {t('saas.wizard.contactEmail')}
                        </label>
                        <input
                          type="email"
                          {...register('contactEmail')}
                          className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                          placeholder={t('saas.wizard.contactEmailPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          {t('saas.wizard.contactPhone')}
                        </label>
                        <input
                          type="tel"
                          {...register('contactPhone')}
                          className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                          placeholder={t('saas.wizard.contactPhonePlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          {t('saas.wizard.contactPosition')}
                        </label>
                        <input
                          type="text"
                          {...register('contactPosition')}
                          className="w-full h-10 px-3 text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                          placeholder={t('saas.wizard.contactPositionPlaceholder')}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 1: Package selection */}
              {currentStep === 1 && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                      {t('saas.wizard.packageTitle')}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('saas.wizard.packageSubtitle')}
                    </p>
                  </div>

                  {/* Billing cycle toggle */}
                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <button
                      type="button"
                      onClick={() => setBillingCycle('monthly')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        billingCycle === 'monthly'
                          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {t('saas.wizard.billingMonthly')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillingCycle('yearly')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        billingCycle === 'yearly'
                          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {t('saas.wizard.billingYearly')} <span className="text-xs opacity-75">{t('saas.wizard.billingYearlyNote')}</span>
                    </button>
                  </div>

                  {/* Package cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
                    {packagesLoading ? (
                      <div className="col-span-full text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f7f75] mx-auto"></div>
                        <p className="mt-2 text-sm text-gray-500">{t('saas.pricing.loadingPackages')}</p>
                      </div>
                    ) : (
                    packages.filter(p => p.isActive).map((pkg) => (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() => setSelectedPackageId(pkg.id)}
                        className={`relative text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                          selectedPackageId === pkg.id
                            ? 'border-[#0f7f75] bg-[#0f7f75]/5 dark:bg-[#0f7f75]/10 ring-2 ring-[#0f7f75]/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        {pkg.isPopular && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-[#0f7f75] text-white text-xs font-medium rounded-full">
                            {t('saas.pricing.popularBadge')}
                          </span>
                        )}
                        <div className="text-center mb-3">
                          <h3 className="font-bold text-gray-900 dark:text-gray-100">{pkg.name}</h3>
                          <p className="text-2xl font-bold text-[#0f7f75] dark:text-teal-300 mt-1">
                            {pkg.price.monthly === 0 ? (
                              t('saas.pricing.free')
                            ) : (
                              <>
                                {billingCycle === 'monthly'
                                  ? pkg.price.monthly.toLocaleString('hu-HU')
                                  : Math.round(pkg.price.yearly / 12).toLocaleString('hu-HU')
                                }
                                <span className="text-sm font-normal text-gray-500"> {t('saas.pricing.perMonth')}</span>
                              </>
                            )}
                          </p>
                          {pkg.price.monthly > 0 && (
                            <p className="text-xs text-gray-400">{t('saas.pricing.plusVAT')}</p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            <strong>{pkg.maxUsers === -1 ? t('saas.pricing.unlimited') : pkg.maxUsers}</strong> {t('saas.pricing.users')}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            <strong>{pkg.maxStorage === -1 ? t('saas.pricing.unlimited') : pkg.maxStorage}</strong> {t('saas.pricing.storage')}
                          </p>
                          <div className="pt-1.5 border-t border-gray-200 dark:border-gray-700">
                            {pkg.features.slice(0, 3).map((feature: string, i: number) => (
                              <p key={i} className="text-xs text-gray-600 dark:text-gray-400 flex items-start">
                                <CheckCircleIcon className="h-3 w-3 mr-1 mt-0.5 text-green-500 flex-shrink-0" />
                                {feature}
                              </p>
                            ))}
                          </div>
                        </div>
                        {selectedPackageId === pkg.id && (
                          <div className="absolute top-2 right-2">
                            <CheckCircleIcon className="h-5 w-5 text-[#0f7f75]" />
                          </div>
                        )}
                      </button>
                    ))
                    )}
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 flex items-start">
                    <SparklesIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {t('saas.wizard.trialInfo')}
                    </p>
                  </div>
                </div>
              )}

              {/* Step 2: Confirmation */}
              {currentStep === 2 && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                      {t('saas.wizard.confirmTitle')}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('saas.wizard.confirmSubtitle')}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('saas.wizard.confirmOrganization')}</h3>
                      <dl className="grid grid-cols-2 gap-2 text-sm">
                        <dt className="text-gray-500 dark:text-gray-400">{t('saas.wizard.confirmName')}</dt>
                        <dd className="text-gray-900 dark:text-gray-100 font-medium">{orgName || '-'}</dd>
                        <dt className="text-gray-500 dark:text-gray-400">{t('saas.wizard.confirmIndustry')}</dt>
                        <dd className="text-gray-900 dark:text-gray-100">{watch('industry') || '-'}</dd>
                        <dt className="text-gray-500 dark:text-gray-400">{t('saas.wizard.confirmTaxNumber')}</dt>
                        <dd className="text-gray-900 dark:text-gray-100">{watch('taxNumber') || '-'}</dd>
                      </dl>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('saas.wizard.confirmPackage')}</h3>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {selectedPackage?.name || '-'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedPackage?.price.monthly === 0 ? t('saas.wizard.confirmFreePackage') : t('saas.wizard.confirmTrial')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-[#0f7f75] dark:text-teal-300">
                            {selectedPackage?.price.monthly === 0
                              ? '0 Ft'
                              : `${(billingCycle === 'monthly'
                                ? selectedPackage?.price.monthly
                                : Math.round((selectedPackage?.price.yearly || 0) / 12)
                              )?.toLocaleString('hu-HU')} ${t('saas.pricing.perMonth')}`
                            }
                            <span className="text-xs text-gray-400"> {t('saas.pricing.plusVAT')}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                        {t('saas.wizard.confirmReady')}
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        {t('saas.wizard.confirmReadyDesc')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={currentStep === 0 || isSubmitting}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  {t('saas.wizard.back')}
                </button>

                {currentStep < STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="flex items-center px-6 py-2.5 text-sm font-medium text-white bg-[#0f7f75] hover:bg-[#0c6b62] rounded-lg  transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {t('saas.wizard.next')}
                    <ArrowRightIcon className="h-4 w-4 ml-1" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center px-6 py-2.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg  transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('saas.wizard.creating')}
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        {t('saas.wizard.createOrg')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
          </>
          )}
        </div>
      </div>

      <div className="py-4 flex justify-center">
        <a href="https://webissimo.eu" target="_blank" rel="noopener noreferrer" className="opacity-60 hover:opacity-100 transition-opacity" aria-label="Webissimo">
          <img src="/webissimo.svg" alt="Webissimo" className="h-4 w-auto invert dark:invert-0" />
        </a>
      </div>
    </div>
  );
};

export default Onboarding;
